import {
  AttachInternals,
  Build,
  Component,
  type ComponentInterface,
  Element,
  Event,
  type EventEmitter,
  Host,
  Prop,
  h,
} from '@stencil/core';
import { WhiteNoSwitchIcon, WhiteToggle, WhiteYesSwitchIcon } from '~/components/common/icon/customSVGs';
import type { IColorTheme } from '~/interfaces';
import { type Attributes, inheritAriaAttributes } from '~/util/DOM';

/**
 * @slot - The label text to associate with the switch. Use the "labelPosition" property
 * to control where the label is placed relative to the switch.
 *
 * @part label - The label text describing the switch.
 * @part toggle - The toggle circle SVG element. You can set color. By default, will inherit button text color.
 * @part button - The button element. You can style background or text color.
 * @part checked - The button element in checked state.
 * @part disabled - The button element in disabled state.
 */
@Component({
  tag: 'ltc-switch',
  styleUrl: 'switch.less',
  shadow: { delegatesFocus: true },
  formAssociated: true,
})
export class LtcSwitch implements ComponentInterface {
  /**
   * 1. Own Properties
   */
  public static counter = 0;
  switchId = `ltc-switch-${LtcSwitch.counter++}`;
  inheritedAttributes: Attributes = {};

  @AttachInternals() internals: ElementInternals;

  /**
   * 2. Reference to host HTML element.
   */
  @Element() host: HTMLLtcSwitchElement;

  /**
   * 3. State() variables
   */

  /**
   * 4. Public Property API
   */
  /**
   * If `true`, the switch is turned ON.
   */
  @Prop({ mutable: true }) checked = false;

  /**
   * If `true`, the user cannot interact with the switch.
   */
  @Prop({ mutable: true }) disabled = false;

  /**
   * The name of the control, which is submitted with the form data.
   */
  @Prop({ mutable: true }) name: string = this.switchId;

  /**
   * Sets the type of control. Text or Icons
   */
  @Prop() type: 'text' | 'icons' = 'text';

  /**
   * Where to place the label relative to the input.
   */
  @Prop({ reflect: true }) labelPosition: 'right' | 'left' = 'right';

  /**
   * How to control the alignment of the toggle and label on the cross axis.<br>
   * `"start"`: The label and control will appear on the left of the cross axis.<br>
   * `"end"`: The label and control will appear on the right of the cross axis.<br>
   * `"center"`: The label and control will appear at the center of the cross axis.<br>
   * This will only have effect in a top and bottom alignment.
   */
  @Prop() alignment: 'start' | 'center' | 'end' = 'center';

  /**
   * Switch color theme
   */
  @Prop({ reflect: true }) theme: IColorTheme = 'white';

  /**
   * Prop lifecycle events
   */

  /**
   * 5. Events section
   */
  /**
   * Emitted when the user switches the toggle on or off.
   *
   * This event will not emit when programmatically setting the `checked` property.
   */
  @Event({ composed: true, bubbles: true }) ltcChange!: EventEmitter<boolean>;

  /**
   * Emitted when the toggle has focus.
   */
  @Event({ composed: true, bubbles: true }) ltcFocus!: EventEmitter<void>;

  /**
   * Emitted when the toggle loses focus.
   */
  @Event({ composed: true, bubbles: true }) ltcBlur!: EventEmitter<void>;

  /**
   * 6. Component lifecycle events
   */
  componentWillLoad(): void {
    this.submitDataToForm();

    this.inheritedAttributes = {
      ...inheritAriaAttributes(this.host),
    };
  }

  /**
   * 7. Listeners
   */

  /**
   * 8. Public methods API
   */

  /**
   * 9. Local methods
   */
  onButtonClick = (): void => {
    if (this.disabled) {
      return;
    }

    this.checked = !this.checked;
    this.submitDataToForm();

    this.ltcChange.emit(this.checked);
  };

  submitDataToForm(): void {
    if (!Build.isServer) {
      const formData = new FormData();
      formData.set(this.name, this.checked.toString());
      this.internals.setFormValue(formData);
    }
  }

  hasLabel(): boolean {
    return this.host.textContent !== '';
  }

  onFocus = (): void => {
    this.ltcFocus.emit();
  };

  onBlur = (): void => {
    this.ltcBlur.emit();
  };

  /**
   * 10. render() function
   */
  render() {
    const { switchId, disabled, checked, type, labelPosition, alignment, theme } = this;

    return (
      <Host>
        <label
          htmlFor={switchId}
          class={{
            'no-label': !this.hasLabel(),
            [`theme-${theme}`]: true,
            [`on-the-${labelPosition}`]: true,
            [`justify-${alignment}`]: true,
          }}
        >
          <span class="label-text" part="label">
            <slot />
          </span>
          <button
            id={switchId}
            type="button"
            role="switch"
            part={`button${checked ? ' checked' : ''}${disabled ? ' disabled' : ''}`}
            {...(disabled ? { disabled } : undefined)}
            aria-checked={checked ? 'true' : 'false'}
            aria-readonly={disabled ? 'true' : 'false'}
            onClick={this.onButtonClick}
            onFocus={this.onFocus}
            onBlur={this.onBlur}
            class={{
              [type]: true,
              checked: checked,
              disabled: disabled,
            }}
            {...this.inheritedAttributes}
          >
            <WhiteToggle svgClass="handle" />
            {type === 'text' && <span class="toggle-text">{checked ? 'YES' : 'NO'}</span>}
            {type === 'icons' && (checked ? <WhiteYesSwitchIcon svgClass="toggle-text" /> : <WhiteNoSwitchIcon svgClass="toggle-text" />)}
          </button>
        </label>
      </Host>
    );
  }
}
